import { APIService } from './../api/api.service';
import { ChartsService } from './charts.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import {
  bufferToggle,
  catchError,
  concatMap,
  filter,
  flatMap,
  map,
  tap,
  windowToggle,
} from 'rxjs/operators';

import { KPI } from '../api';
import { InitiativesService } from './initiatives.service';
import { ErrorService } from './error.service';

@Injectable({ providedIn: 'root' })
export class InitiativesChangesService {

  public pendingChangesNumber$ = new ReplaySubject<number>(1);
  public autoSave$ = new BehaviorSubject<boolean>(true);

  private change$: Subject<Partial<KPI.Initiative>>;
  private save$ = new Subject<void>();
  private pendingChangesNumber: number = 0;

  constructor(
    private errorService: ErrorService,
    private initiativesService: InitiativesService,
    private chartsService: ChartsService,
    private apiService: APIService,
  ) {
    this.initChanges();
  }

  public addChanges(investId: number, changes: {property: string, value: any}[]): void {
    const change: Partial<KPI.Initiative> = { id: investId };
    changes.forEach((c) => change[c.property] = c.value);

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public addChange(initiativeId: number, property: string, value: any): void {
    const change: Partial<KPI.Initiative> = { id: initiativeId };
    change[property] = value;

    this.change$.next(change);
    this.updatePendingChanges(1);
  }

  public save(): void {
    this.save$.next();
    this.autoSave$.next(this.autoSave$.value);
  }

  private initChanges(): void {
    this.change$ = new Subject<Partial<KPI.Initiative>>();
    this.saveChanges();
    this.pendingChangesNumber = 0;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
  }

  private autoSaveOn$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === true));
  }

  private autoSaveOff$(): Observable<boolean> {
    return this.autoSave$.pipe(filter(autoSave => autoSave === false));
  }

  private saveChanges(): Subscription {
    return merge(
      this.change$.pipe(bufferToggle(this.autoSaveOff$(), () => merge(this.autoSaveOn$(), this.save$))),
      this.change$.pipe(windowToggle(this.autoSaveOn$(), () => this.autoSaveOff$())),
    ).pipe(
      flatMap((initiative) => initiative),
      concatMap((initiative) => {
        const { gains } = initiative;
        let newGains = gains;
        if (!!gains) {
          newGains = gains.map(gain => {
            // remove the theoretical_savings_first_year and theoretical_greenhouse_gas_savings_first_year fields
            // since we calculate it on the backend
            const { theoretical_savings_first_year, theoretical_greenhouse_gas_savings_first_year, ...rest } = gain;
            return rest;
          });
        }
        const newInitiative = { ...initiative, gains: newGains };
        return this.initiativesService.updateInitiative(
          initiative.id,
          newInitiative,
        ).pipe(
          catchError((error) => {
            this.errorService.handleError(error, true);
            return of(null);
          }),
          map((r) => r),
        )
      }),
      tap(() => this.updatePendingChanges(-1)),
    ).subscribe(
      () => {
      },
    );
  }

  private updatePendingChanges(increment: number): void {
    this.pendingChangesNumber += increment;
    this.pendingChangesNumber$.next(this.pendingChangesNumber);
    // Force energy charts / cards to refresh after an initiative has been updated
    if (this.pendingChangesNumber === 0) {
      setTimeout(() => this.chartsService.energyHasChanged(), 500)
    }
  }

  public updateMultipleInitiativesStatus(
    initiatives: number[],
    current_status: number,
    new_status: number,
    scenario: number,
  ) {
    return this.apiService.initiatives_status_change({
      initiatives, current_status, new_status, scenario
    }).pipe(
      tap(() => this.chartsService.energyHasChanged()),
    );
  }

  public updateInitiativeInstance(
    initiativeId: number,
    instance: KPI.UpdatableInitiativeInstance,
  ): void {
    this.addChange(initiativeId, 'instances', [instance]);
  }
}
